/**
 * Reply-channel availability — the pure logic behind the member Conversation
 * composer's channel selector.
 *
 * The composer can reply on WhatsApp, Email, or Instagram. Each channel is
 * available only when the member has the corresponding reply target on file:
 *   - WhatsApp  → a phone (E.164), dispatched via `send_whatsapp_message`.
 *   - Email     → an email address, dispatched via `send_gmail`.
 *   - Instagram → an IGSID from `vendor_links`, dispatched via `send_instagram_dm`.
 *
 * Extracted from the composer so the availability + default-selection rules are
 * unit-testable without rendering React.
 */

import { isPlaceholderEmail } from "@layer/canonical-data/placeholder-email";

export type ReplyChannel = "whatsapp" | "email" | "instagram";

export interface ReplyTargets {
  /** E.164 phone, or null when the member has no phone. */
  readonly phone: string | null;
  /** Email address, or null when the member has no email. */
  readonly email: string | null;
  /** Instagram-scoped id (from vendor_links), or null when unlinked. */
  readonly igsid: string | null;
}

/** A target counts as present only when it's a non-empty, non-whitespace string. */
function present(value: string | null): boolean {
  return value !== null && value.trim().length > 0;
}

/**
 * The channels the composer can send on, in a fixed display order
 * (WhatsApp, Email, Instagram). A channel appears only when its reply target is
 * present. Empty when the member has no reply target on any channel.
 *
 * A synthetic channel-signup placeholder (`<digits>@whatsapp.placeholder.…`)
 * does NOT count as an email target: `send_gmail` now skips those recipients
 * server-side, so offering the pill would stage a reply that silently goes
 * nowhere — WhatsApp-signup members are reachable on WhatsApp instead.
 */
export function availableReplyChannels(
  targets: ReplyTargets,
): ReadonlyArray<ReplyChannel> {
  const out: ReplyChannel[] = [];
  const email = targets.email;
  if (present(targets.phone)) out.push("whatsapp");
  if (email !== null && email.trim().length > 0 && !isPlaceholderEmail(email)) {
    out.push("email");
  }
  if (present(targets.igsid)) out.push("instagram");
  return out;
}

/**
 * The channel the composer selects by default: the first available one (WhatsApp
 * preferred, then Email, then Instagram). Falls back to `'whatsapp'` when no
 * channel is available (the composer renders its empty state in that case).
 */
export function defaultReplyChannel(targets: ReplyTargets): ReplyChannel {
  return availableReplyChannels(targets)[0] ?? "whatsapp";
}
